import { Component } from "react"
import PropTypes from "prop-types"
import { omit, keysIn, some, isEmpty, isBoolean, includes, get } from "lodash"
import { TextField, FormControl, DialogContent, DialogActions, Button, Dialog, DialogTitle, IconButton, InputLabel } from "@mui/material"
import { Close, Cached, Clear } from "@mui/icons-material"
import UnifiedSearch from "modules/unified-search"
import DatePicker from "components/date-form-picker"
import { validateTopicName } from "helpers/topics"
import { MODEL } from "helpers/shapes"
import { STEPS, disableSearchResult } from "./helpers"
import { DATE_FORMAT } from "helpers/dates"
import SelectedPosition from "components/selected-position"
import ModelSelectorAutocomplete from "connected-components/model-selector-autocomplete"

import "./style.scss"

const allowedTypes = ["security", "portfolio"]

class ResearchTopicCreate extends Component {
  static propTypes = {
    models: PropTypes.arrayOf(MODEL),
    modelId: PropTypes.string.isRequired,
    account: PropTypes.string.isRequired,
    instrument: PropTypes.object,
    availability: PropTypes.object,
    selectedDate: PropTypes.string,
    createTopic: PropTypes.func,
    applyAction: PropTypes.func,
    setResearchTopicModel: PropTypes.func,
    setModel: PropTypes.func,
    setSelectedDate: PropTypes.func,
    resetSelectedDate: PropTypes.func,
    setInstrument: PropTypes.func,
    step: PropTypes.number.isRequired,
    setStep: PropTypes.func.isRequired,
    creating: PropTypes.bool.isRequired,
    closeDialog: PropTypes.func.isRequired,
    defaultName: PropTypes.string,
    defaultDescription: PropTypes.string,
  }

  constructor(props) {
    super(props)
    const errors = {}

    const state = {
      isSearchOpened: false,
      modelId: props.modelId,
      name: props.defaultName || "",
      description: props.defaultDescription || "",
      selectedDate: props.selectedDate || "",
      dateError: null,
    }

    if (state.name && !validateTopicName(state.name)) {
      errors.name = true
    }

    this.state = { ...state, errors, submitDisabled: true }
  }

  handleResearchTopicModelChange = (modelId) => {
    const { setModel: RTsetModel } = this.props
    this.setState({ modelId })
    RTsetModel(modelId)
  }

  computeDisabledButton = () => {
    const { instrument, step, creating } = this.props
    const { name, modelId, selectedDate, errors, dateError } = this.state

    const checks = [
      isEmpty(instrument), // instrument should not be empty
      isEmpty(modelId), // modelId should not be empty
    ]
    if (step > 0) {
      checks.push(
        !isEmpty(errors), // topic information errors
        isEmpty(selectedDate) || dateError,
        isEmpty(name) || !validateTopicName(name),
        creating,
      )
    }

    return some(checks, (value) => isBoolean(value) ? value : isEmpty(value))
  }

  onChange = event => {
    const { value, name: fieldName } = event.target
    const changes = {
      errors: { ...this.state.errors },
    }

    if (fieldName !== "name") {
      changes[fieldName] = value
    } else {
      changes.name = value

      if (!validateTopicName(value) && !changes.errors.name) {
        changes.errors.name = true
      } else if (validateTopicName(value) && changes.errors[fieldName]) {
        changes.errors = omit(changes.errors, fieldName)
      }
    }

    this.setState({ ...this.state, ...changes })
  }

  onDateChange = (date) => {
    const { setSelectedDate } = this.props
    setSelectedDate(date)
    this.setState({ selectedDate: date, dateError: false })
  }

  onDateError = () => {
    this.setState({ dateError: true })
  }

  onKeyPress = event => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      const errors = this.validateTopic()
      if (keysIn(errors).length === 0) {
        this.submitTopic()
      }
    }
  }

  isUnmodeledDate = (date) => {
    const { unmodeledDates } = this.props.availability ?? {}
    const dayOfTheWeek = date.day()
    // 6 = Saturday, 0 = Sunday
    return includes([0, 6], dayOfTheWeek) || get(unmodeledDates, date.format(DATE_FORMAT), false)
  }

  validateTopic = () => {
    const { name, dateError } = this.state
    const errors = {}

    if (!validateTopicName(name)) {
      errors.name = true
    }
    if (dateError) {
      errors.date = true
    }

    return errors
  }

  submitTopic = () => {
    const errors = this.validateTopic()
    if (keysIn(errors).length > 0) {
      this.setState({ errors, submitDisabled: true })
      return
    }
    const { instrument, createTopic } = this.props
    const { name, selectedDate, description } = this.state
    createTopic({ name, selectedDate, instrument, description })
  }

  searchItemsActions = () => {
    const { setInstrument } = this.props
    return ({
      security: {
        noDispatch: true,
        mainAction: item => () => {
          setInstrument({ ...item })
        },
      },
      portfolio: {
        noDispatch: true,
        mainAction: item => () => {
          setInstrument({ ...item })
        },
      },
    })
  }

  modelSelector = () => {
    const { models } = this.props
    const { modelId } = this.state
    return <FormControl variant="standard" fullWidth>
      <ModelSelectorAutocomplete
        fullWidth
        className="model-selector"
        models={models}
        value={modelId}
        onChange={this.handleResearchTopicModelChange}
        label="Model"
      />
    </FormControl>
  }

  unifiedSearch = () => {
    const { modelId, account } = this.props

    return (<UnifiedSearch
      autoFocus
      account={account}
      modelId={modelId}
      itemsActions={this.searchItemsActions()}
      disableResult={disableSearchResult}
      allowedTypes={allowedTypes}
    />)
  }

  selectedInstrument = () => {
    const { instrument, setInstrument, step } = this.props
    if (isEmpty(instrument)) { return null }

    let color
    let icon = <Clear className="red" />

    if (step > 0) {
      color = "secondary"
      icon = <Cached />
    }

    return <SelectedPosition id="topic-instrument" position={instrument} onIconClick={() => setInstrument({})} icon={icon} iconColor={color} />
  }

  renderInstrumentStep = () => {
    const { instrument } = this.props
    return <>
      {this.modelSelector()}
      <FormControl variant="standard">
        <InputLabel disableAnimation shrink>
          Reference Instrument
        </InputLabel>
        {isEmpty(instrument) ? this.unifiedSearch() : this.selectedInstrument()}
      </FormControl>
    </>
  }

  renderTopicStep = () => {
    const { availability, step } = this.props
    const { name, description, selectedDate, errors } = this.state
    const { earliestDate, latestDate } = availability || {}

    return <>
      <FormControl variant="standard">
        <InputLabel disableAnimation shrink>
          Reference Instrument
        </InputLabel>
        {this.selectedInstrument(step)}
      </FormControl>
      <FormControl variant="standard" id="research-topic-create-date">
        <DatePicker
          selectedDate={selectedDate}
          onChange={this.onDateChange}
          onError={this.onDateError}
          label="Selected Date"
          earliestDate={earliestDate}
          latestDate={latestDate}
          shouldDisableDate={this.isUnmodeledDate}
          slotProps={{
            openPickerButton: {
              "data-test-class": "CalendarIcon",
            },
          }}
        />
      </FormControl>
      <FormControl variant="standard">
        <TextField
          variant="standard"
          autoFocus={!!selectedDate}
          id="topic-name"
          margin="normal"
          name="name"
          label="Research topic name"
          value={name}
          error={errors.name}
          onChange={this.onChange}
          onKeyDown={this.onKeyPress}
          helperText={errors.name ? "Invalid name. Please follow naming guidelines." : "3-50 chars long. Alphanum, _, -, and spaces."}
          slotProps={{
            formHelperText: { error: errors.name, classes: { root: "topic-name-error" } },
          }}
        />
      </FormControl>
      <FormControl variant="standard">
        <TextField
          variant="standard"
          id="topic-description"
          margin="normal"
          name="description"
          multiline
          label="Research topic description"
          value={description}
          onChange={this.onChange}
          onKeyDown={this.onKeyPress}
        />
      </FormControl>
    </>
  }

  renderCurrentStep = () => {
    const { step } = this.props
    if (STEPS[step].id === "instrument") {
      return this.renderInstrumentStep()
    }
    if (STEPS[step].id === "topic") {
      return this.renderTopicStep()
    }
  }

  dialogTitle = () => {
    const { step } = this.props
    if (step === 0) { return "Choose Your Reference Instrument" }
    return "Create Research Topic For..."
  }

  render() {
    const { closeDialog, step, setStep } = this.props
    const currentStep = STEPS[step]
    const { submitButton, cancelButton } = currentStep
    const submitAction = step === 0 ? () => setStep(step + 1) : this.submitTopic
    return <Dialog className="research-topic-create-dialog" open onClose={closeDialog}>
      <DialogTitle>
        {this.dialogTitle()}
        <IconButton aria-label="close" onClick={closeDialog} size="large">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent className="modules_topic-create-content">
        <div className="flex-wrapper">
          {this.renderCurrentStep()}
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={closeDialog}>{cancelButton}</Button>
        {step === 1
          && <Button color="primary" onClick={() => setStep(step - 1)}>Previous</Button>}

        <Button color="primary" disabled={this.computeDisabledButton()} onClick={submitAction}>{submitButton}</Button>
      </DialogActions>
    </Dialog>
  }
}

export default ResearchTopicCreate
